/**
 * File Upload Security Validation
 * Implements comprehensive validation for all file uploads
 */

import { logger } from '@/lib/logger';

// File type configurations
const FILE_CONFIGS = {
  // Image files
  image: {
    extensions: ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'],
    mimeTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'],
    maxSize: 10 * 1024 * 1024, // 10MB
    magicBytes: {
      jpeg: [0xff, 0xd8, 0xff],
      png: [0x89, 0x50, 0x4e, 0x47],
      gif: [0x47, 0x49, 0x46],
      webp: [0x52, 0x49, 0x46, 0x46], // RIFF header
    },
  },

  // CAD files
  cad: {
    extensions: ['.dwg', '.dxf', '.dgn', '.dwf'],
    mimeTypes: [
      'application/acad',
      'application/x-acad',
      'application/autocad_dwg',
      'image/x-dwg',
      'application/dwg',
      'application/x-dwg',
      'application/x-autocad',
      'drawing/dwg',
      'image/vnd.dwg',
      'application/dxf',
      'application/x-dxf',
      'text/plain', // DXF files are often text
      'application/octet-stream', // Generic binary
    ],
    maxSize: 100 * 1024 * 1024, // 100MB
    magicBytes: {
      dwg: [0x41, 0x43], // AC (AutoCAD)
      dxf: [0x30, 0x0d, 0x0a], // 0\r\n (DXF text format)
    },
  },

  // PDF files
  pdf: {
    extensions: ['.pdf'],
    mimeTypes: ['application/pdf'],
    maxSize: 50 * 1024 * 1024, // 50MB
    magicBytes: {
      pdf: [0x25, 0x50, 0x44, 0x46], // %PDF
    },
  },

  // JSON files
  json: {
    extensions: ['.json'],
    mimeTypes: ['application/json', 'text/json', 'text/plain'],
    maxSize: 5 * 1024 * 1024, // 5MB
    validateContent: true,
  },
};

// Malicious patterns to check in filenames
const MALICIOUS_PATTERNS = [
  /\.\./g, // Directory traversal
  /[<>:"|?*]/g, // Invalid characters
  /^\./, // Hidden files
  /\0/, // Null bytes
  // eslint-disable-next-line no-control-regex
  /[^\x01-\x7F]/g, // Non-ASCII characters (optional, might be too strict)
];

// Suspicious file content patterns
const SUSPICIOUS_CONTENT = [
  /<script[\s>]/gi, // Script tags
  /javascript:/gi, // JavaScript protocol
  /on\w+\s*=/gi, // Event handlers
  /<iframe/gi, // Iframes
  /<embed/gi, // Embedded content
  /<object/gi, // Object tags
];

export interface FileValidationOptions {
  type: keyof typeof FILE_CONFIGS;
  allowMultiple?: boolean;
  customMaxSize?: number;
  strictMode?: boolean; // Extra security checks
}

export interface ValidationResult {
  valid: boolean;
  error?: string;
  sanitizedName?: string;
  warnings?: string[];
}

/**
 * Validate file type by extension and MIME type
 */
function validateFileType(
  file: File,
  config: (typeof FILE_CONFIGS)[keyof typeof FILE_CONFIGS],
): boolean {
  const fileName = file.name.toLowerCase();
  const fileExt = fileName.substring(fileName.lastIndexOf('.'));

  // Check extension
  if (!config.extensions.includes(fileExt)) {
    return false;
  }

  // Check MIME type
  if (!config.mimeTypes.includes(file.type) && file.type !== '') {
    // Some systems don't set MIME type correctly, especially for CAD files
    logger.warn(
      `File MIME type mismatch: expected ${config.mimeTypes.join(', ')}, got ${file.type}`,
    );
    // Don't reject CAD files with empty or wrong MIME types
    if (config !== FILE_CONFIGS.cad) {
      return false;
    }
  }

  return true;
}

/**
 * Validate file size
 */
function validateFileSize(file: File, maxSize: number): boolean {
  return file.size <= maxSize;
}

/**
 * Check file content for magic bytes (file signature)
 */
async function validateMagicBytes(
  file: File,
  config: (typeof FILE_CONFIGS)[keyof typeof FILE_CONFIGS],
): Promise<boolean> {
  if (!config.magicBytes) {
    return true; // No magic bytes to check
  }

  // Read first few bytes of the file
  const buffer = await file.slice(0, 16).arrayBuffer();
  const bytes = new Uint8Array(buffer);

  // Check against known magic bytes
  for (const [format, signature] of Object.entries(config.magicBytes)) {
    const matches = signature.every((byte, index) => bytes[index] === byte);
    if (matches) {
      return true;
    }
  }

  logger.warn(`File magic bytes don't match expected signatures for ${file.name}`);
  return false;
}

/**
 * Sanitize filename to prevent security issues
 */
export function sanitizeFileName(fileName: string): string {
  // Remove path components
  let sanitized = fileName.split(/[/\\]/).pop() || 'unnamed';

  // Remove dangerous patterns
  MALICIOUS_PATTERNS.forEach(pattern => {
    sanitized = sanitized.replace(pattern, '');
  });

  // Replace spaces with underscores
  sanitized = sanitized.replace(/\s+/g, '_');

  // Limit length
  if (sanitized.length > 255) {
    const ext = sanitized.substring(sanitized.lastIndexOf('.'));
    sanitized = sanitized.substring(0, 255 - ext.length) + ext;
  }

  // Ensure it has an extension
  if (!sanitized.includes('.')) {
    sanitized += '.unknown';
  }

  return sanitized;
}

/**
 * Check file content for suspicious patterns (for text-based files)
 */
async function checkSuspiciousContent(file: File): Promise<string[]> {
  const warnings: string[] = [];

  // Only check text-based files
  if (!file.type.includes('text') && !file.type.includes('svg') && !file.type.includes('json')) {
    return warnings;
  }

  try {
    const text = await file.text();

    SUSPICIOUS_CONTENT.forEach(pattern => {
      if (pattern.test(text)) {
        warnings.push(`Suspicious pattern detected: ${pattern.source}`);
      }
    });
  } catch (error) {
    logger.error('Error checking file content:', error);
  }

  return warnings;
}

/**
 * Validate JSON file content
 */
async function validateJsonContent(file: File): Promise<boolean> {
  try {
    const text = await file.text();
    JSON.parse(text);
    return true;
  } catch (error) {
    logger.error('Invalid JSON content:', error);
    return false;
  }
}

/**
 * Main validation function
 */
export async function validateFile(
  file: File,
  options: FileValidationOptions,
): Promise<ValidationResult> {
  const config = FILE_CONFIGS[options.type];

  if (!config) {
    return {
      valid: false,
      error: `Invalid file type configuration: ${options.type}`,
    };
  }

  // Check file exists and has content
  if (!file || file.size === 0) {
    return {
      valid: false,
      error: 'File is empty or invalid',
    };
  }

  // Validate file type
  if (!validateFileType(file, config)) {
    return {
      valid: false,
      error: `Invalid file type. Allowed types: ${config.extensions.join(', ')}`,
    };
  }

  // Validate file size
  const maxSize = options.customMaxSize || config.maxSize;
  if (!validateFileSize(file, maxSize)) {
    return {
      valid: false,
      error: `File too large. Maximum size: ${(maxSize / (1024 * 1024)).toFixed(2)}MB`,
    };
  }

  // Sanitize filename
  const sanitizedName = sanitizeFileName(file.name);

  // Check for suspicious patterns in filename
  if (file.name !== sanitizedName) {
    logger.warn(`Filename was sanitized: ${file.name} -> ${sanitizedName}`);
  }

  // Strict mode checks
  if (options.strictMode) {
    // Validate magic bytes
    const validMagicBytes = await validateMagicBytes(file, config);
    if (!validMagicBytes) {
      return {
        valid: false,
        error: 'File content does not match expected format',
        sanitizedName,
      };
    }

    // Check for suspicious content
    const warnings = await checkSuspiciousContent(file);
    if (warnings.length > 0) {
      logger.warn('Suspicious content detected:', warnings);
      // In strict mode, reject files with suspicious content
      return {
        valid: false,
        error: 'File contains potentially malicious content',
        warnings,
        sanitizedName,
      };
    }
  }

  // Validate JSON content if applicable
  if (config.validateContent && options.type === 'json') {
    const validJson = await validateJsonContent(file);
    if (!validJson) {
      return {
        valid: false,
        error: 'Invalid JSON content',
        sanitizedName,
      };
    }
  }

  return {
    valid: true,
    sanitizedName,
    warnings: [],
  };
}

/**
 * Validate multiple files
 */
export async function validateFiles(
  files: File[],
  options: FileValidationOptions,
): Promise<ValidationResult[]> {
  if (!options.allowMultiple && files.length > 1) {
    return [
      {
        valid: false,
        error: 'Multiple files not allowed',
      },
    ];
  }

  const results = await Promise.all(files.map(file => validateFile(file, options)));

  return results;
}

/**
 * Create a validated File object with sanitized name
 */
export function createSanitizedFile(file: File, sanitizedName: string): File {
  return new File([file], sanitizedName, {
    type: file.type,
    lastModified: file.lastModified,
  });
}

/**
 * Security audit log for file uploads
 */
export function logFileUploadAttempt(
  file: File,
  result: ValidationResult,
  userId?: string,
  orgId?: string,
): void {
  const logEntry = {
    timestamp: new Date().toISOString(),
    fileName: file.name,
    fileSize: file.size,
    fileType: file.type,
    validationResult: result,
    userId,
    orgId,
    action: result.valid ? 'ALLOWED' : 'BLOCKED',
  };

  if (!result.valid) {
    logger.error('File upload blocked:', logEntry);
  } else {
    logger.info('File upload allowed:', logEntry);
  }

  // TODO: Send to security monitoring service
  // sendToSecurityMonitoring(logEntry);
}
